'use client'

import { useEffect, useRef } from 'react'

import { emitNotification, dismissNotification } from '@lib/notifications'
import type { NotificationPayload } from '@lib/notifications'

interface ConditionalNotificationConfig {
  /** Stable notification ID — used for emit and dismiss */
  id: string
  /** When true the notification is shown; when false it's auto-dismissed */
  isActive: boolean
  /** Notification payload when active. Ignored when isActive is false. */
  payload: NotificationPayload | null
}

/**
 * Condition-bound notification: emits while `isActive` is true,
 * auto-dismisses when the condition resolves.
 *
 * The notification lifecycle is owned entirely by the condition —
 * manual dismiss from the bell UI won't prevent re-emission on the
 * next render if the condition is still true.
 */
export function useConditionalNotification({ id, isActive, payload }: ConditionalNotificationConfig): void {
  const prevActiveRef = useRef(false)
  // Stringify payload to use as a stable primitive dependency — avoids
  // unnecessary effect runs when the object reference changes but content is identical
  const payloadJson = payload ? JSON.stringify(payload) : ''

  useEffect(() => {
    if (isActive && payload) {
      emitNotification(payload)
    } else if (!isActive && prevActiveRef.current) {
      dismissNotification(id)
    }

    prevActiveRef.current = isActive
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isActive, payloadJson])
}
